import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Search, SlidersHorizontal, GitCompare, ShieldCheck } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VehicleCard from "@/components/VehicleCard";
import VehicleModal from "@/components/VehicleModal";
import CompareModal from "@/components/CompareModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, isPriceNotDecided } from "@/utils/vehiclePrice";
import {
  BODY_GROUPS,
  emptyFilters,
  queryFromState,
  stateFromSearchParams,
} from "@/utils/inventoryFiltersUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_COMPARE = 3;

const parseRangeNumber = (raw) => {
  const n = parseInt(String(raw).replace(/[^0-9-]/g, ""), 10);
  return Number.isNaN(n) ? null : n;
};

function RangeSliderFilter({
  label,
  minBound,
  maxBound,
  minValue,
  maxValue,
  onCommit,
  step = 1,
  prefix = "",
  suffix = "",
  testId,
  isDark,
  labelCls,
  sliderTrackCls,
  sliderRangeCls,
  sliderThumbCls,
}) {
  const [local, setLocal] = useState([minValue, maxValue]);
  const [minText, setMinText] = useState(String(minValue));
  const [maxText, setMaxText] = useState(String(maxValue));
  const draggingRef = useRef(false);

  useEffect(() => {
    if (draggingRef.current) return;
    setLocal([minValue, maxValue]);
    setMinText(String(minValue));
    setMaxText(String(maxValue));
  }, [minValue, maxValue]);

  const clampPair = (a, b) => {
    const loBound = Number(minBound) || 0;
    const hiBound = Number(maxBound) || 0;
    let lo = Math.min(Math.max(loBound, a), hiBound);
    let hi = Math.min(Math.max(loBound, b), hiBound);
    if (lo > hi) [lo, hi] = [hi, lo];
    return [lo, hi];
  };

  const commit = (a, b) => {
    const [lo, hi] = clampPair(a, b);
    setLocal([lo, hi]);
    setMinText(String(lo));
    setMaxText(String(hi));
    if (lo !== minValue || hi !== maxValue) onCommit(lo, hi);
  };

  const applyMinText = () => {
    const parsed = parseRangeNumber(minText);
    commit(parsed == null ? local[0] : parsed, local[1]);
  };

  const applyMaxText = () => {
    const parsed = parseRangeNumber(maxText);
    commit(local[0], parsed == null ? local[1] : parsed);
  };

  const fieldCls = `h-8 w-full rounded-none px-2 text-xs tabular-nums ${
    isDark
      ? "bg-black/50 border border-white/10 text-white"
      : "bg-white border border-gray-300 text-gray-900"
  }`;

  return (
    <div className="space-y-2">
      <label className={labelCls}>{label}</label>
      <div className="px-1">
        <Slider
          value={local}
          onValueChange={(next) => {
            draggingRef.current = true;
            setLocal(next);
            setMinText(String(next[0]));
            setMaxText(String(next[1]));
          }}
          onValueCommit={([min, max]) => {
            draggingRef.current = false;
            commit(min, max);
          }}
          min={minBound}
          max={maxBound}
          step={step}
          minStepsBetweenThumbs={0}
          className="py-4"
          trackClassName={sliderTrackCls}
          rangeClassName={`${sliderRangeCls} transition-none`}
          thumbClassName={`${sliderThumbCls} transition-none`}
          data-testid={testId}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <span className={`block text-[10px] mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>Min</span>
          <div className="relative">
            {prefix ? <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>{prefix}</span> : null}
            <input
              type="text"
              inputMode="numeric"
              value={minText}
              onChange={(e) => setMinText(e.target.value)}
              onBlur={applyMinText}
              onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
              className={`${fieldCls} ${prefix ? "pl-5" : ""}`}
              data-testid={`${testId}-min-input`}
            />
            {suffix ? <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>{suffix}</span> : null}
          </div>
        </div>
        <div>
          <span className={`block text-[10px] mb-1 ${isDark ? "text-gray-500" : "text-gray-500"}`}>Max</span>
          <div className="relative">
            {prefix ? <span className={`absolute left-2 top-1/2 -translate-y-1/2 text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>{prefix}</span> : null}
            <input
              type="text"
              inputMode="numeric"
              value={maxText}
              onChange={(e) => setMaxText(e.target.value)}
              onBlur={applyMaxText}
              onKeyDown={(e) => { if (e.key === "Enter") { e.currentTarget.blur(); } }}
              className={`${fieldCls} ${prefix ? "pl-5" : ""} ${suffix ? "pr-7" : ""}`}
              data-testid={`${testId}-max-input`}
            />
            {suffix ? <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>{suffix}</span> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  labelMap,
  allLabel = "All",
  leadingOptions = [],
  testId,
  labelCls,
  selectTriggerCls,
  selectContentCls,
  selectItemCls,
  optionDisplayLabel,
}) {
  return (
    <div className="space-y-2">
      <label className={labelCls}>{label}</label>
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className={selectTriggerCls} data-testid={testId}><SelectValue placeholder={allLabel} /></SelectTrigger>
        <SelectContent className={selectContentCls}>
          <SelectItem value="all" className={selectItemCls}>{allLabel}</SelectItem>
          {leadingOptions.map((opt) => (
            <SelectItem key={opt.value} value={opt.value} className={selectItemCls}>
              {opt.label}
            </SelectItem>
          ))}
          {(options || []).map((opt) => (
            <SelectItem key={opt} value={opt} className={selectItemCls}>
              {optionDisplayLabel(opt, labelMap)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function PaginationBar({
  className = "",
  totalPages,
  currentPage,
  onPageChange,
  isDark,
  pageNavBtnCls,
  prevLabel,
  nextLabel,
}) {
  if (totalPages <= 1) return null;
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <Button
        type="button"
        variant="outline"
        disabled={currentPage <= 1}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        className={pageNavBtnCls}
      >
        {prevLabel}
      </Button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => onPageChange(page)}
          className={`min-w-[2.5rem] h-10 px-3 text-sm font-['Manrope'] ${
            page === currentPage
              ? isDark ? "bg-blue-600 text-white" : "bg-red-600 text-white"
              : isDark
                ? "border border-white/15 text-white/80 hover:bg-white/10"
                : "border border-gray-300 text-gray-900 hover:bg-gray-50"
          }`}
        >
          {page}
        </button>
      ))}
      <Button
        type="button"
        variant="outline"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        className={pageNavBtnCls}
      >
        {nextLabel}
      </Button>
    </div>
  );
}

const defaultFilters = {
  make: "", model: "", trim: "", condition: "",
  bodyGroup: "", bodyType: "", fuelType: "", transmission: "", drivetrain: "", cylinders: "",
  exteriorColor: "", interiorColor: "",
  minPrice: 0, maxPrice: 100000,
  minYear: 2020, maxYear: 2025,
  minMileage: 0, maxMileage: 200000,
  searchQuery: ""
};

const defaultFilterOptions = {
  makes: [],
  models: [],
  trims: [],
  years: [],
  fuel_types: [],
  transmissions: [],
  body_types: [],
  drivetrains: [],
  cylinders: [],
  exterior_colors: [],
  interior_colors: [],
  conditions: [],
  body_types_es: {},
  exterior_colors_es: {},
  interior_colors_es: {},
  price_range: { min: 0, max: 100000 },
  year_range: { min: 2020, max: 2025 },
  mileage_range: { min: 0, max: 200000 },
};

const readRange = (range, fallbackMin, fallbackMax) => {
  const min = Number(range?.min);
  const max = Number(range?.max);
  return {
    min: Number.isFinite(min) ? min : fallbackMin,
    max: Number.isFinite(max) ? max : fallbackMax,
  };
};

const normalizeFilterOptions = (raw) => {
  const data = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
  return {
    ...defaultFilterOptions,
    ...data,
    price_range: readRange(data.price_range, 0, 100000),
    year_range: readRange(data.year_range, 2020, 2025),
    mileage_range: readRange(data.mileage_range, 0, 200000),
  };
};

export default function InventoryPage() {
  const { t, language } = useTranslation();
  const { isDark } = useTheme();
  const [, setSearchParams] = useSearchParams();
  const urlReady = useRef(false);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
  const [filterOptions, setFilterOptions] = useState(null);
  const [sortBy, setSortBy] = useState("price-desc");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);

  // Filter options, then the address bar, so a shared link is not wiped by the defaults.
  useEffect(() => {
    let cancelled = false;
    const fetchOptions = async () => {
      try {
        const res = await axios.get(`${API}/vehicles/filter-options`);
        if (cancelled) return;
        const options = normalizeFilterOptions(res.data);
        const parsed = stateFromSearchParams(window.location.search, options);
        setFilterOptions(options);
        setDraftFilters(parsed.filters);
        setAppliedFilters(parsed.filters);
        setSortBy(parsed.sort);
        setPageSize(parsed.pageSize);
        setCurrentPage(parsed.page);
        urlReady.current = true;
      } catch (error) {
        console.error("Error fetching filter options:", error);
      }
    };
    fetchOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!urlReady.current || !filterOptions) return;
    const next = queryFromState({
      filters: appliedFilters,
      options: filterOptions,
      sort: sortBy,
      page: currentPage,
      pageSize,
    });
    const current = window.location.search.replace(/^\?/, "");
    if (next === current) return;
    setSearchParams(new URLSearchParams(next), { replace: true });
  }, [appliedFilters, filterOptions, sortBy, currentPage, pageSize, setSearchParams]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.make) params.append("make", appliedFilters.make);
      if (appliedFilters.model) params.append("model", appliedFilters.model);
      if (appliedFilters.trim) params.append("trim", appliedFilters.trim);
      if (appliedFilters.condition) params.append("condition", appliedFilters.condition);
      if (appliedFilters.bodyGroup) params.append("body", appliedFilters.bodyGroup);
      else if (appliedFilters.bodyType) params.append("body_type", appliedFilters.bodyType);
      if (appliedFilters.fuelType) params.append("fuel_type", appliedFilters.fuelType);
      if (appliedFilters.transmission) params.append("transmission", appliedFilters.transmission);
      if (appliedFilters.drivetrain) params.append("drivetrain", appliedFilters.drivetrain);
      if (appliedFilters.cylinders) params.append("cylinders", appliedFilters.cylinders);
      if (appliedFilters.exteriorColor) params.append("exterior_color", appliedFilters.exteriorColor);
      if (appliedFilters.interiorColor) params.append("interior_color", appliedFilters.interiorColor);
      if (filterOptions) {
        if (appliedFilters.minPrice > filterOptions.price_range.min) params.append("min_price", appliedFilters.minPrice);
        if (appliedFilters.maxPrice < filterOptions.price_range.max) params.append("max_price", appliedFilters.maxPrice);
        if (appliedFilters.minYear > filterOptions.year_range.min) params.append("min_year", appliedFilters.minYear);
        if (appliedFilters.maxYear < filterOptions.year_range.max) params.append("max_year", appliedFilters.maxYear);
        if (appliedFilters.minMileage > filterOptions.mileage_range.min) params.append("min_mileage", appliedFilters.minMileage);
        if (appliedFilters.maxMileage < filterOptions.mileage_range.max) params.append("max_mileage", appliedFilters.maxMileage);
      }
      if (appliedFilters.searchQuery) params.append("keyword", appliedFilters.searchQuery);

      const response = await axios.get(`${API}/vehicles?${params.toString()}`);
      let data = Array.isArray(response.data) ? response.data : [];

      const pricedSort = (a, b, dir) => {
        const aTbd = isPriceNotDecided(a);
        const bTbd = isPriceNotDecided(b);
        if (aTbd && bTbd) return 0;
        if (aTbd) return 1;
        if (bTbd) return -1;
        return dir * ((Number(a.price) || 0) - (Number(b.price) || 0));
      };
      if (sortBy === "price-asc") data.sort((a, b) => pricedSort(a, b, 1));
      else if (sortBy === "price-desc") data.sort((a, b) => pricedSort(a, b, -1));
      else if (sortBy === "year-desc") data.sort((a, b) => b.year - a.year);
      else if (sortBy === "mileage-asc") data.sort((a, b) => a.mileage - b.mileage);

      data.sort((a, b) => Number(Boolean(a.list_at_end)) - Number(Boolean(b.list_at_end)));

      setVehicles(data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, sortBy, filterOptions]);

  useEffect(() => { if (filterOptions) fetchVehicles(); }, [fetchVehicles, filterOptions]);

  const totalPages = Math.max(1, Math.ceil(vehicles.length / pageSize));
  const pagedVehicles = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return vehicles.slice(start, start + pageSize);
  }, [vehicles, currentPage, pageSize]);

  useEffect(() => {
    if (loading) return;
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages, loading]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [currentPage]);

  const resetFilters = () => {
    const next = emptyFilters(filterOptions || defaultFilterOptions);
    setDraftFilters(next);
    setAppliedFilters(next);
    setSortBy("price-desc");
    setPageSize(DEFAULT_PAGE_SIZE);
    setCurrentPage(1);
    setMobileFiltersOpen(false);
  };

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setCurrentPage(1);
    setMobileFiltersOpen(false);
    setMoreFiltersOpen(false);
  };

  const openVehicleModal = (vehicle) => { setSelectedVehicle(vehicle); setModalOpen(true); };

  const toggleCompare = useCallback((vehicle) => {
    setCompareList(prev => {
      const exists = prev.some(v => v.id === vehicle.id);
      if (exists) return prev.filter(v => v.id !== vehicle.id);
      if (prev.length < MAX_COMPARE) return [...prev, vehicle];
      toast.error(t('compare.compareUpTo'));
      return prev;
    });
  }, [t]);
  const removeFromCompare = useCallback((vehicleId) => { setCompareList(prev => prev.filter(v => v.id !== vehicleId)); }, []);
  const clearCompareList = useCallback(() => { setCompareList([]); }, []);
  const isVehicleSelected = useCallback((vehicleId) => compareList.some(v => v.id === vehicleId), [compareList]);

  const moreFiltersActiveCount = useMemo(() => {
    let n = 0;
    if (draftFilters.condition) n += 1;
    if (draftFilters.bodyGroup || draftFilters.bodyType) n += 1;
    if (draftFilters.exteriorColor) n += 1;
    if (draftFilters.interiorColor) n += 1;
    if (draftFilters.fuelType) n += 1;
    if (draftFilters.cylinders) n += 1;
    if (draftFilters.drivetrain) n += 1;
    if (draftFilters.transmission) n += 1;
    return n;
  }, [draftFilters]);

  const clearMoreFilters = () => {
    setDraftFilters((f) => ({
      ...f,
      condition: "",
      bodyGroup: "",
      bodyType: "",
      exteriorColor: "",
      interiorColor: "",
      fuelType: "",
      cylinders: "",
      drivetrain: "",
      transmission: "",
    }));
  };

  // Shared style classes
  const selectTriggerCls = `h-10 rounded-none text-sm ${isDark ? 'bg-black/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const selectContentCls = isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200';
  const selectItemCls = isDark ? 'text-white' : 'text-gray-900';
  const labelCls = `text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`;
  const sliderTrackCls = isDark ? '' : 'bg-[#800020]/20';
  const sliderRangeCls = isDark ? '' : 'bg-[#800020]';
  const sliderThumbCls = isDark ? '' : 'border-[#800020]';
  const inputCls = `pl-9 h-10 rounded-none text-sm ${isDark ? 'bg-black/50 border-white/10 text-white placeholder:text-gray-600' : 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'}`;
  const applyBtnCls = `w-full h-11 rounded-none font-semibold uppercase tracking-wider text-sm text-white ${
    isDark ? "bg-blue-600 hover:bg-blue-700" : "bg-red-600 hover:bg-red-700"
  }`;
  const pageNavBtnCls = `rounded-none ${
    isDark
      ? "border-white/15 text-white hover:bg-white/10 hover:text-white"
      : "border-gray-300 text-gray-900 hover:bg-gray-50 hover:text-gray-900"
  }`;

  // Hardcoded enum labels for Spanish (filter values stay English for API queries)
  const hardcodedOptionLabel = (raw) => {
    if (raw == null || raw === "") return raw;
    const key = String(raw).trim().toLowerCase().replace(/[-_]+/g, " ").replace(/\s+/g, " ");
    const map = {
      new: "inventory.values.conditionNew",
      used: "inventory.values.conditionUsed",
      "certified pre owned": "inventory.values.conditionCertified",
      "certified pre-owned": "inventory.values.conditionCertified",
      "certified preowned": "inventory.values.conditionCertified",
      cpo: "inventory.values.conditionCertified",
      gasoline: "inventory.values.fuelGasoline",
      gas: "inventory.values.fuelGasoline",
      petrol: "inventory.values.fuelGasoline",
      electric: "inventory.values.fuelElectric",
      diesel: "inventory.values.fuelDiesel",
      hybrid: "inventory.values.fuelHybrid",
      automatic: "inventory.values.transmissionAutomatic",
      auto: "inventory.values.transmissionAutomatic",
      fwd: "inventory.values.drivetrainFwd",
      rwd: "inventory.values.drivetrainRwd",
      awd: "inventory.values.drivetrainAwd",
      "4wd": "inventory.values.drivetrain4wd",
      "front wheel drive": "inventory.values.drivetrainFront",
      "front-wheel drive": "inventory.values.drivetrainFront",
      "rear wheel drive": "inventory.values.drivetrainRear",
      "rear-wheel drive": "inventory.values.drivetrainRear",
      "all wheel drive": "inventory.values.drivetrainAll",
      "all-wheel drive": "inventory.values.drivetrainAll",
      "four wheel drive": "inventory.values.drivetrainFour",
      "four-wheel drive": "inventory.values.drivetrainFour",
    };
    const path = map[key];
    if (!path) return String(raw);
    const translated = t(path);
    return translated.startsWith("inventory.values.") ? String(raw) : translated;
  };

  /** Prefer API-saved Spanish label map, then hardcoded enums, then raw English. */
  const optionDisplayLabel = (raw, esMap) => {
    if (raw == null || raw === "") return raw;
    if (language === "es" && esMap && typeof esMap === "object") {
      const es = esMap[raw];
      if (typeof es === "string" && es.trim()) return es.trim();
    }
    return hardcodedOptionLabel(raw);
  };

  const selectUi = { labelCls, selectTriggerCls, selectContentCls, selectItemCls, optionDisplayLabel };

  const renderMoreFiltersFields = () => {
    if (!filterOptions) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FilterSelect {...selectUi}
          label={t("inventory.condition")}
          value={draftFilters.condition}
          onChange={(v) => setDraftFilters((f) => ({ ...f, condition: v }))}
          options={filterOptions.conditions || []}
          allLabel={t("inventory.allConditions")}
          testId="condition-filter"
        />
        <FilterSelect {...selectUi}
          label={t("inventory.bodyType")}
          value={draftFilters.bodyGroup ? `group:${draftFilters.bodyGroup}` : draftFilters.bodyType}
          onChange={(v) => setDraftFilters((f) => (
            v.startsWith("group:")
              ? { ...f, bodyGroup: v.slice(6), bodyType: "" }
              : { ...f, bodyType: v, bodyGroup: "" }
          ))}
          leadingOptions={BODY_GROUPS.map((item) => ({
            value: `group:${item.value}`,
            label: t(item.labelKey),
          }))}
          options={filterOptions.body_types || []}
          labelMap={filterOptions.body_types_es}
          allLabel={t("inventory.allBodyTypes")}
          testId="body-type-filter"
        />
        <FilterSelect {...selectUi}
          label={t("inventory.exteriorColor")}
          value={draftFilters.exteriorColor}
          onChange={(v) => setDraftFilters((f) => ({ ...f, exteriorColor: v }))}
          options={filterOptions.exterior_colors || []}
          labelMap={filterOptions.exterior_colors_es}
          allLabel={t("inventory.allColors")}
          testId="color-filter"
        />
        <FilterSelect {...selectUi}
          label={t("inventory.interior")}
          value={draftFilters.interiorColor}
          onChange={(v) => setDraftFilters((f) => ({ ...f, interiorColor: v }))}
          options={filterOptions.interior_colors || []}
          labelMap={filterOptions.interior_colors_es}
          allLabel={t("inventory.allInteriors")}
          testId="interior-filter"
        />
        <FilterSelect {...selectUi}
          label={t('inventory.fuelType')}
          value={draftFilters.fuelType}
          onChange={(v) => setDraftFilters((f) => ({ ...f, fuelType: v }))}
          options={filterOptions.fuel_types || []}
          allLabel={t('inventory.allTypes')}
          testId="fuel-filter"
        />
        <FilterSelect {...selectUi}
          label={t("inventory.cylinders")}
          value={draftFilters.cylinders}
          onChange={(v) => setDraftFilters((f) => ({ ...f, cylinders: v }))}
          options={filterOptions.cylinders || []}
          allLabel={t("inventory.allCylinders")}
          testId="cylinders-filter"
        />
        <FilterSelect {...selectUi}
          label={t("inventory.drivetrain")}
          value={draftFilters.drivetrain}
          onChange={(v) => setDraftFilters((f) => ({ ...f, drivetrain: v }))}
          options={filterOptions.drivetrains || []}
          allLabel={t("inventory.allDrivetrains")}
          testId="drivetrain-filter"
        />
        <FilterSelect {...selectUi}
          label={t('vehicleModal.transmission')}
          value={draftFilters.transmission}
          onChange={(v) => setDraftFilters((f) => ({ ...f, transmission: v }))}
          options={filterOptions.transmissions || []}
          allLabel={t("inventory.allTransmissions")}
          testId="transmission-filter"
        />
      </div>
    );
  };

  const paginationProps = {
    totalPages,
    currentPage,
    onPageChange: setCurrentPage,
    isDark,
    pageNavBtnCls,
    prevLabel: t("inventory.prevPage"),
    nextLabel: t("inventory.nextPage"),
  };

  const renderFilterPanel = (isMobile = false) => {
    if (!filterOptions) return null;
    return (
      <div className={`space-y-5 ${isMobile ? "" : "sticky top-24"}`}>
        <div className="flex items-center justify-between">
          <h3 className={`font-unbounded text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('inventory.filters')}</h3>
          <Button variant="ghost" size="sm" onClick={resetFilters}
            className={`hover:bg-transparent p-0 h-auto ${isDark ? 'text-blue-500 hover:text-blue-400' : 'text-red-600 hover:text-red-700'}`}
            data-testid="reset-filters-btn">
            {t('inventory.resetAll')}
          </Button>
        </div>

        <Button
          type="button"
          onClick={applyFilters}
          className={applyBtnCls}
          data-testid={isMobile ? "apply-filters-mobile-top-btn" : "apply-filters-top-btn"}
        >
          {t("inventory.applyFilters")}
        </Button>

        <Separator className={isDark ? 'bg-white/10' : 'bg-gray-200'} />

        {/* 1. Keyword */}
        <div className="space-y-2">
          <label className={labelCls}>{t('inventory.keyword')}</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              type="text"
              placeholder={t('inventory.search')}
              value={draftFilters.searchQuery}
              onChange={(e) => setDraftFilters((f) => ({ ...f, searchQuery: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyFilters();
                }
              }}
              className={inputCls}
              data-testid="search-input"
            />
          </div>
        </div>

        {/* 2. Make */}
        <FilterSelect {...selectUi}
          label={t('inventory.make')}
          value={draftFilters.make}
          onChange={(v) => setDraftFilters((f) => ({ ...f, make: v, model: "", trim: "" }))}
          options={filterOptions.makes || []}
          allLabel={t('inventory.allMakes')}
          testId="make-filter"
        />

        {/* 3. Model */}
        <FilterSelect {...selectUi}
          label={t('inventory.model')}
          value={draftFilters.model}
          onChange={(v) => setDraftFilters((f) => ({ ...f, model: v }))}
          options={filterOptions.models || []}
          allLabel={t('inventory.allModels')}
          testId="model-filter"
        />

        {/* 4. Trim */}
        <FilterSelect {...selectUi}
          label={t('inventory.trim')}
          value={draftFilters.trim}
          onChange={(v) => setDraftFilters((f) => ({ ...f, trim: v }))}
          options={filterOptions.trims || []}
          allLabel={t('inventory.allTrims')}
          testId="trim-filter"
        />

        {/* 5. Price Range */}
        <RangeSliderFilter
          label={t('inventory.priceRange')}
          minBound={filterOptions.price_range.min}
          maxBound={filterOptions.price_range.max}
          minValue={draftFilters.minPrice}
          maxValue={draftFilters.maxPrice}
          onCommit={(min, max) => setDraftFilters((f) => ({ ...f, minPrice: min, maxPrice: max }))}
          step={1}
          prefix="$"
          testId="price-slider"
          isDark={isDark}
          labelCls={labelCls}
          sliderTrackCls={sliderTrackCls}
          sliderRangeCls={sliderRangeCls}
          sliderThumbCls={sliderThumbCls}
        />

        {/* 6. Year (inventory-based min/max selects) */}
        <div className="space-y-2">
          <label className={labelCls}>{t('inventory.year')}</label>
          <div className="grid grid-cols-2 gap-2">
            <Select
              value={draftFilters.minYear.toString()}
              onValueChange={(v) => setDraftFilters((f) => ({ ...f, minYear: parseInt(v, 10) }))}
            >
              <SelectTrigger className={selectTriggerCls} data-testid="min-year-filter"><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentCls}>
                {(filterOptions.years || []).map((y) => (
                  <SelectItem key={y} value={y.toString()} className={selectItemCls}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={draftFilters.maxYear.toString()}
              onValueChange={(v) => setDraftFilters((f) => ({ ...f, maxYear: parseInt(v, 10) }))}
            >
              <SelectTrigger className={selectTriggerCls} data-testid="max-year-filter"><SelectValue /></SelectTrigger>
              <SelectContent className={selectContentCls}>
                {(filterOptions.years || []).map((y) => (
                  <SelectItem key={y} value={y.toString()} className={selectItemCls}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* 7. Mileage */}
        <RangeSliderFilter
          label={t('inventory.mileage')}
          minBound={filterOptions.mileage_range.min}
          maxBound={filterOptions.mileage_range.max}
          minValue={draftFilters.minMileage}
          maxValue={draftFilters.maxMileage}
          onCommit={(min, max) => setDraftFilters((f) => ({ ...f, minMileage: min, maxMileage: max }))}
          step={1}
          suffix="mi"
          testId="mileage-slider"
          isDark={isDark}
          labelCls={labelCls}
          sliderTrackCls={sliderTrackCls}
          sliderRangeCls={sliderRangeCls}
          sliderThumbCls={sliderThumbCls}
        />

        <Separator className={isDark ? 'bg-white/10' : 'bg-gray-200'} />

        {/* More Filters → popup */}
        <Button
          type="button"
          variant="outline"
          onClick={() => setMoreFiltersOpen(true)}
          className={`w-full h-11 rounded-none font-semibold uppercase tracking-wider text-sm ${
            isDark
              ? 'border-white/15 text-white hover:bg-white/10 hover:text-white'
              : 'border-gray-300 text-gray-900 hover:bg-gray-50 hover:text-gray-900'
          }`}
          data-testid="more-filters-btn"
        >
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          {t("inventory.moreFilters")}
          {moreFiltersActiveCount > 0 && (
            <span
              className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-xs font-bold text-white ${
                isDark ? 'bg-blue-600' : 'bg-red-600'
              }`}
            >
              {moreFiltersActiveCount}
            </span>
          )}
        </Button>

        <Button
          type="button"
          onClick={applyFilters}
          className={applyBtnCls}
          data-testid={isMobile ? "apply-filters-mobile-bottom-btn" : "apply-filters-bottom-btn"}
        >
          {t("inventory.applyFilters")}
        </Button>
      </div>
    );
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`} data-testid="inventory-page">
      <Navbar />
      
      {/* Header */}
      <div className={`pt-24 pb-8 ${isDark ? 'radial-gradient' : ''}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h1 className={`font-unbounded text-3xl md:text-4xl lg:text-5xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('inventory.title')}
            </h1>
            <p className={`text-lg max-w-2xl ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('inventory.description')}
            </p>
            <div
              className={`mt-6 w-full flex gap-3 md:gap-4 p-4 md:p-5 border-l-4 ${
                isDark
                  ? "bg-[#143028] border-blue-500"
                  : "bg-[#E7F6EC] border-red-600"
              }`}
              role="note"
              data-testid="inventory-quality-notice"
            >
              <ShieldCheck
                className={`h-6 w-6 md:h-7 md:w-7 flex-shrink-0 mt-0.5 ${isDark ? "text-blue-400" : "text-red-600"}`}
                aria-hidden="true"
              />
              <p className={`font-['Manrope'] text-base md:text-lg font-bold leading-snug ${isDark ? "text-white" : "text-gray-900"}`}>
                {t("inventory.qualityNotice")}
              </p>
            </div>
          </motion.div>

          {/* Sort Bar */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-8 flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3 justify-end">
              <PaginationBar className="mr-auto" {...paginationProps} />
              <Select value={sortBy} onValueChange={(value) => { setSortBy(value); setCurrentPage(1); }}>
                <SelectTrigger className={`h-12 w-48 rounded-none ${isDark ? 'bg-black/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-testid="sort-select">
                  <SelectValue placeholder={t('inventory.sortBy')} />
                </SelectTrigger>
                <SelectContent className={selectContentCls}>
                  <SelectItem value="price-asc" className={selectItemCls}>{t('inventory.priceLowHigh')}</SelectItem>
                  <SelectItem value="price-desc" className={selectItemCls}>{t('inventory.priceHighLow')}</SelectItem>
                  <SelectItem value="year-desc" className={selectItemCls}>{t('inventory.newestFirst')}</SelectItem>
                  <SelectItem value="mileage-asc" className={selectItemCls}>{t('inventory.lowestMileage')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                <SelectTrigger className={`h-12 w-44 rounded-none ${isDark ? 'bg-black/50 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900'}`} data-testid="page-size-select">
                  <SelectValue placeholder={t('inventory.perPage')} />
                </SelectTrigger>
                <SelectContent className={selectContentCls}>
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <SelectItem key={n} value={String(n)} className={selectItemCls}>
                      {n} {t('inventory.perPage')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" className={`lg:hidden h-12 rounded-none ${isDark ? 'border-white/10 text-white hover:bg-white/10 hover:text-white' : 'border-gray-300 text-gray-900 hover:bg-gray-100 hover:text-gray-900'}`} data-testid="mobile-filter-btn">
                    <SlidersHorizontal className="h-5 w-5 mr-2" />{t('inventory.filters')}
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className={`w-80 ${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'}`}>
                  <SheetHeader><SheetTitle className={`font-unbounded ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('inventory.filters')}</SheetTitle></SheetHeader>
                  <div className="mt-6 overflow-y-auto max-h-[calc(100vh-100px)] filter-scroll pr-2">
                    {renderFilterPanel(true)}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <div className="flex gap-8">
          {/* Desktop Filters */}
          <motion.aside initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
            className="hidden lg:block w-72 flex-shrink-0">
            <div className={`p-6 ${isDark ? 'bg-[#0A0A0A] border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
              {renderFilterPanel(false)}
            </div>
          </motion.aside>

          {/* Vehicle Grid */}
          <div className="flex-1">
            <div className="mb-6 flex items-center justify-between">
              <div className={isDark ? 'text-gray-400' : 'text-gray-600'}>
                <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{vehicles.length}</span> {t('inventory.vehiclesFound')}
                {vehicles.length > 0 && (
                  <span className="ml-2 text-sm">
                    · {t('inventory.page')} {currentPage} {t('inventory.of')} {totalPages}
                  </span>
                )}
              </div>
              {compareList.length > 0 && (
                <button onClick={clearCompareList}
                  className={`text-sm transition-colors ${isDark ? 'text-gray-500 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}
                  data-testid="clear-compare-btn">
                  {t('inventory.clearSelection')} ({compareList.length})
                </button>
              )}
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className={`animate-pulse ${isDark ? 'bg-[#0A0A0A] border border-white/10' : 'bg-white border border-gray-200 shadow-sm'}`}>
                    <div className={`aspect-[4/3] ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`} />
                    <div className="p-6 space-y-4">
                      <div className={`h-4 rounded w-1/3 ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`} />
                      <div className={`h-6 rounded w-2/3 ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`} />
                      <div className={`h-4 rounded w-1/2 ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`} />
                    </div>
                  </div>
                ))}
              </div>
            ) : vehicles.length === 0 ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
                <p className={`text-lg mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('inventory.noVehicles')}</p>
                <Button onClick={resetFilters}
                  className={`text-white rounded-none ${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}
                  data-testid="reset-filters-empty-btn">
                  {t('inventory.resetFilters')}
                </Button>
              </motion.div>
            ) : (
              <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <AnimatePresence mode="popLayout">
                  {pagedVehicles.map((vehicle, index) => (
                    <motion.div key={vehicle.id} layout initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3, delay: index * 0.05 }}>
                      <VehicleCard
                        vehicle={vehicle}
                        onClick={() => openVehicleModal(vehicle)}
                        showCompare
                        isSelected={isVehicleSelected(vehicle.id)}
                        onToggleCompare={() => toggleCompare(vehicle)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
              <PaginationBar className="mt-8 justify-center" {...paginationProps} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Compare Floating Button */}
      <AnimatePresence>
        {compareList.length >= 2 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-8 right-8 z-40">
            <button onClick={() => setCompareModalOpen(true)}
              className={`flex items-center gap-3 px-8 py-4 text-white font-semibold shadow-2xl transition-all ${isDark ? 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/30 hover:shadow-blue-600/50' : 'bg-red-600 hover:bg-red-700 shadow-red-600/30 hover:shadow-red-600/50'}`}
              data-testid="compare-btn">
              <GitCompare className="w-5 h-5" />
              {t('inventory.compare')} ({compareList.length})
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />

      <VehicleModal vehicle={selectedVehicle} open={modalOpen} onClose={() => { setModalOpen(false); setSelectedVehicle(null); }} />
      <CompareModal vehicles={compareList} open={compareModalOpen} onClose={() => setCompareModalOpen(false)} onRemoveVehicle={removeFromCompare} />

      {/* More Filters popup */}
      <Dialog open={moreFiltersOpen} onOpenChange={setMoreFiltersOpen}>
        <DialogContent
          className={`max-w-2xl max-h-[90vh] overflow-y-auto rounded-none p-0 gap-0 ${
            isDark
              ? 'bg-[#0A0A0A] border-white/10 text-white'
              : 'bg-white border-gray-200 text-gray-900'
          }`}
          data-testid="more-filters-dialog"
        >
          <DialogHeader className={`px-6 py-5 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <DialogTitle className={`font-unbounded text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t("inventory.moreFilters")}
            </DialogTitle>
            <DialogDescription className={isDark ? 'text-gray-400' : 'text-gray-500'}>
              {t("inventory.moreFiltersDescription")}
            </DialogDescription>
          </DialogHeader>

          <div className="px-6 py-6">
            {renderMoreFiltersFields()}
          </div>

          <div className={`px-6 py-4 border-t flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
            <Button
              type="button"
              variant="ghost"
              onClick={clearMoreFilters}
              className={`rounded-none justify-start px-0 ${isDark ? 'text-gray-400 hover:text-white hover:bg-transparent' : 'text-gray-500 hover:text-gray-900 hover:bg-transparent'}`}
              data-testid="clear-more-filters-btn"
            >
              {t("inventory.clearExtraFilters")}
              {moreFiltersActiveCount > 0 ? ` (${moreFiltersActiveCount})` : ''}
            </Button>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setMoreFiltersOpen(false)}
                className={`rounded-none flex-1 sm:flex-none ${isDark ? 'border-white/15 text-white hover:bg-white/10 hover:text-white' : 'border-gray-300 text-gray-900 hover:bg-gray-50 hover:text-gray-900'}`}
              >
                {t("inventory.cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  applyFilters();
                  setMoreFiltersOpen(false);
                }}
                className={`rounded-none text-white flex-1 sm:flex-none min-w-[8rem] ${
                  isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                }`}
                data-testid="apply-more-filters-btn"
              >
                {t("inventory.applyFilters")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
